import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Header from "./Components/Header";
import Menu from "./Components/Menu";
import { config } from "./Components/Config";
import { jwtFetch } from "./utils/apiClient";
import { Modal, OverlayTrigger, Tooltip, Dropdown, Tabs, Tab } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill-new";
import 'react-quill-new/dist/quill.snow.css';
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

// Register table toolbar icon (prevents Quill from rendering a blank/double button)
const Icons = Quill.import('ui/icons');
Icons['table'] = `<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
  <rect class="ql-stroke" height="12" rx="1" ry="1" width="12" x="3" y="3"/>
  <line class="ql-stroke" x1="3" x2="15" y1="7.5" y2="7.5"/>
  <line class="ql-stroke" x1="7.5" x2="7.5" y1="7.5" y2="15"/>
</svg>`;

// --- Quill Table Embed Blot ---
const BlockEmbed = Quill.import('blots/block/embed');

class TableEmbed extends BlockEmbed {
    static create(html) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        node.innerHTML = html;
        node.querySelectorAll('table').forEach(t => {
            t.style.cssText = 'border-collapse:collapse;width:100%;margin:4px 0;';
        });
        node.querySelectorAll('td,th').forEach(cell => {
            cell.removeAttribute('contenteditable');
            cell.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
        });
        node.querySelectorAll('th').forEach(th => {
            th.style.backgroundColor = '#f8f9fa';
            th.style.fontWeight = '600';
        });
        return node;
    }

    static value(domNode) {
        const clone = domNode.cloneNode(true);
        clone.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
        return clone.innerHTML;
    }

    attach() {
        super.attach();
        const editorEl = this.scroll.domNode;
        const isEditMode = editorEl?.getAttribute('contenteditable') !== 'false';

        if (isEditMode) {
            this.domNode.querySelectorAll('td, th').forEach(cell => {
                cell.setAttribute('contenteditable', 'true');
            });

            this._onInput = () => {
                try { this.scroll.update(); } catch (e) { }
            };
            this.domNode.addEventListener('input', this._onInput, true);

            this._onFocusIn = (e) => {
                const cell = e.target.closest('td, th');
                if (!cell) return;
                const scroll = this.scroll;
                this.domNode.dispatchEvent(
                    new CustomEvent('quill-table-cell-focus', { bubbles: true, detail: { cell, scroll } })
                );
            };
            this.domNode.addEventListener('focusin', this._onFocusIn);
            this._onKeyDown = (e) => {
                if (!this.domNode.contains(e.target)) return;
                const cell = e.target.closest('td, th');
                if (!cell) return;

                if (e.key === 'Enter' && !e.shiftKey) {
                    const row = cell.closest('tr');
                    const table = row?.closest('table');
                    if (!table) { e.stopImmediatePropagation(); return; }
                    const allRows = table.querySelectorAll('tr');
                    if (row === allRows[allRows.length - 1]) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        const colCount = row.cells.length;
                        const newRow = document.createElement('tr');
                        for (let i = 0; i < colCount; i++) {
                            const td = document.createElement('td');
                            td.setAttribute('contenteditable', 'true');
                            td.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
                            td.innerHTML = ' ';
                            newRow.appendChild(td);
                        }
                        row.after(newRow);
                        newRow.cells[0]?.focus();
                        try { this.scroll.update(); } catch (err) { }
                        return;
                    }
                }

                e.stopImmediatePropagation();
            };
            document.addEventListener('keydown', this._onKeyDown, true);

            this._onBeforeInput = (e) => {
                if (!this.domNode.contains(e.target)) return;
                if (!e.target.closest('td, th')) return;
                e.stopImmediatePropagation();
            };
            document.addEventListener('beforeinput', this._onBeforeInput, true);
        }
    }

    detach() {
        if (this._onInput) this.domNode.removeEventListener('input', this._onInput, true);
        if (this._onFocusIn) this.domNode.removeEventListener('focusin', this._onFocusIn);
        if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown, true);
        if (this._onBeforeInput) document.removeEventListener('beforeinput', this._onBeforeInput, true);
        super.detach();
    }
}

TableEmbed.blotName = 'table-embed';
TableEmbed.tagName = 'div';
TableEmbed.className = 'ql-table-embed';
Quill.register(TableEmbed, true);

const TABLE_GRID_SIZE = 8;

const TablePicker = ({ onSelect }) => {
    const [hover, setHover] = useState({ r: 0, c: 0 });
    const [customRows, setCustomRows] = useState('');
    const [customCols, setCustomCols] = useState('');

    const handleCustomInsert = () => {
        const r = parseInt(customRows, 10);
        const c = parseInt(customCols, 10);
        if (r > 0 && c > 0) onSelect(r, c);
    };

    return (
        <div
            className="ql-table-picker"
            style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }}
        >
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '6px', textAlign: 'center', minWidth: '148px' }}>
                {hover.r > 0 ? `${hover.r} × ${hover.c} table` : 'Select table size'}
            </div>
            <div onMouseLeave={() => setHover({ r: 0, c: 0 })}>
                {Array.from({ length: TABLE_GRID_SIZE }, (_, r) => (
                    <div key={r} style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                        {Array.from({ length: TABLE_GRID_SIZE }, (_, c) => (
                            <div
                                key={c}
                                onMouseEnter={() => setHover({ r: r + 1, c: c + 1 })}
                                onClick={() => onSelect(r + 1, c + 1)}
                                style={{
                                    width: '16px', height: '16px', border: '1px solid', borderRadius: '2px', cursor: 'pointer', flexShrink: 0,
                                    borderColor: r < hover.r && c < hover.c ? '#0d6efd' : '#ced4da',
                                    backgroundColor: r < hover.r && c < hover.c ? '#cfe2ff' : '#fff',
                                }}
                            />
                        ))}
                    </div>
                ))}
            </div>
            <div style={{ borderTop: '1px solid #dee2e6', marginTop: '8px', paddingTop: '8px' }}>
                <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Custom size:</div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input
                        type="number" min="1" value={customRows}
                        onChange={e => setCustomRows(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustomInsert()}
                        placeholder="Rows"
                        className="form-control form-control-sm"
                        style={{ width: '56px', fontSize: '12px' }}
                    />
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>×</span>
                    <input
                        type="number" min="1" value={customCols}
                        onChange={e => setCustomCols(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustomInsert()}
                        placeholder="Cols"
                        className="form-control form-control-sm"
                        style={{ width: '56px', fontSize: '12px' }}
                    />
                    <button onClick={handleCustomInsert} className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        Insert
                    </button>
                </div>
            </div>
        </div>
    );
};

// Tooltip Component
const TooltipInfo = ({ text }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{text}</Tooltip>}
    >
        <span className="ms-1" style={{ cursor: 'pointer' }}>
            <i className="bi bi-info-circle text-secondary" style={{ fontSize: '0.8rem' }}></i>
        </span>
    </OverlayTrigger>
);

const SUPPORT_TAB_STORAGE_KEY = "supportEventStatus";
const VALID_EVENT_TABS = new Set(["all", "upcoming", "done", "canceled"]);

const getSavedEventTab = () => {
    const saved = localStorage.getItem(SUPPORT_TAB_STORAGE_KEY);
    if (saved && VALID_EVENT_TABS.has(saved)) {
        return saved;
    }
    return "all";
};

const Event = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [tableFilters, setTableFilters] = useState(() => ({ eventStatus: getSavedEventTab() }));
    const [tableSearch, setTableSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [feedbackFormImagePreview, setFeedbackFormImagePreview] = useState(null);
    const [upcomingEvent, setUpcomingEvent] = useState(0);
    const [allEvent, setAllEvent] = useState(0);
    const [doneEvent, setDoneEvent] = useState(0);
    const [canceledEvent, setCanceledEvent] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });

    // Modal & Form States
    const [showModal, setShowModal] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showPromptExample, setShowPromptExample] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [isSaving, setIsSaving] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [dateError, setDateError] = useState('');
    const crmUser = JSON.parse(localStorage.getItem('crmUser'));

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        promptAi: '',
        address: '',
        type: '',
        link: '',
        addressLink: '',
        show: '',
        timeType: '',
        startDate: '',
        endDate: '',
        image: null,
        waitingSubject: '',
        waitingText: '',
        acceptSubject: '',
        acceptText: '',
        rejectSubject: '',
        rejectText: '',
        feedbackSubject: '',
        feedbackText: '',
        calenderText: '',
        feedbackLink: '',
        invitationSubject: '',
        invitationText: '',
        checkInManual: false,
        checkInQr: false,
        checkInFace: false,
        feedbackFormImage: null,
        feedbackFormDescription: '',
        feedbackFormQuestions: [],
        registrationFormDescription: '',
        registrationFormQuestions: []
    });

    const [tablePickerOpen, setTablePickerOpen] = useState(false);
    const [tablePickerPos, setTablePickerPos] = useState({ top: 0, left: 0 });
    const activeQuillRef = useRef(null);
    const [tableFocusState, setTableFocusState] = useState({ visible: false, cellEl: null, scroll: null, top: 0, left: 0 });
    const tableFocusRef = useRef(null);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'image'],
                ['table'],
                ['clean'],
            ],
            handlers: {
                table: function () {
                    activeQuillRef.current = this.quill;
                    const toolbar = this.quill.getModule('toolbar');
                    const btn = toolbar?.container?.querySelector('.ql-table');
                    if (btn) {
                        const rect = btn.getBoundingClientRect();
                        setTablePickerPos({ top: rect.bottom + 4, left: rect.left });
                    }
                    setTablePickerOpen(prev => !prev);
                },
            },
        },
    }), []);

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list',
        'link', 'image',
        'table-embed',
    ];

    const handleInsertTable = useCallback((rows, cols) => {
        const quill = activeQuillRef.current;
        if (!quill) return;

        let html = '<table><thead><tr>';
        for (let c = 0; c < cols; c++) html += `<th>Header ${c + 1}</th>`;
        html += '</tr></thead><tbody>';
        for (let r = 0; r < rows; r++) {
            html += '<tr>';
            for (let c = 0; c < cols; c++) html += '<td> </td>';
            html += '</tr>';
        }
        html += '</tbody></table>';

        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, 'table-embed', html, 'user');
        quill.setSelection(range.index + 1, 0);
        setTablePickerOpen(false);
    }, []);

    const insertRowAbove = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const colCount = row.cells.length;
        const newRow = document.createElement('tr');
        for (let i = 0; i < colCount; i++) {
            const td = document.createElement('td');
            td.setAttribute('contenteditable', 'true');
            td.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
            td.innerHTML = ' ';
            newRow.appendChild(td);
        }
        row.parentNode.insertBefore(newRow, row);
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    const insertRowBelow = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const colCount = row.cells.length;
        const newRow = document.createElement('tr');
        for (let i = 0; i < colCount; i++) {
            const td = document.createElement('td');
            td.setAttribute('contenteditable', 'true');
            td.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
            td.innerHTML = ' ';
            newRow.appendChild(td);
        }
        row.after(newRow);
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    const insertColLeft = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const table = row.closest('table');
        const colIndex = Array.from(row.cells).indexOf(cellEl);
        Array.from(table.querySelectorAll('tr')).forEach(r => {
            const td = document.createElement('td');
            td.setAttribute('contenteditable', 'true');
            td.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
            td.innerHTML = ' ';
            const ref = r.cells[colIndex];
            ref ? r.insertBefore(td, ref) : r.appendChild(td);
        });
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    const insertColRight = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const table = row.closest('table');
        const colIndex = Array.from(row.cells).indexOf(cellEl);
        Array.from(table.querySelectorAll('tr')).forEach(r => {
            const td = document.createElement('td');
            td.setAttribute('contenteditable', 'true');
            td.style.cssText = 'border:1px solid #dee2e6;padding:6px 10px;min-width:80px;vertical-align:top;';
            td.innerHTML = ' ';
            const ref = r.cells[colIndex + 1];
            ref ? r.insertBefore(td, ref) : r.appendChild(td);
        });
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    const deleteRow = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const table = row.closest('table');
        if (table.querySelectorAll('tr').length <= 1) return;
        row.remove();
        setTableFocusState(prev => ({ ...prev, visible: false }));
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    const deleteCol = useCallback(() => {
        const { cellEl, scroll } = tableFocusState;
        if (!cellEl) return;
        const row = cellEl.closest('tr');
        if (!row) return;
        const table = row.closest('table');
        const colIndex = Array.from(row.cells).indexOf(cellEl);
        const rows = Array.from(table.querySelectorAll('tr'));
        if (rows[0] && rows[0].cells.length <= 1) return;
        rows.forEach(r => { const c = r.cells[colIndex]; if (c) c.remove(); });
        setTableFocusState(prev => ({ ...prev, visible: false }));
        try { scroll.update(); } catch (e) { }
    }, [tableFocusState]);

    useEffect(() => {
        if (!tablePickerOpen) return;
        const close = (e) => {
            if (!e.target.closest('.ql-table-picker') && !e.target.closest('.ql-table')) {
                setTablePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [tablePickerOpen]);

    useEffect(() => {
        const handler = (e) => {
            const { cell, scroll } = e.detail;
            const rect = cell.getBoundingClientRect();
            setTableFocusState({ visible: true, cellEl: cell, scroll, top: rect.bottom + 4, left: rect.left });
        };
        document.addEventListener('quill-table-cell-focus', handler);
        return () => document.removeEventListener('quill-table-cell-focus', handler);
    }, []);

    useEffect(() => {
        if (!tableFocusState.visible) return;
        const handleMouseDown = (e) => {
            const isOnToolbar = tableFocusRef.current && tableFocusRef.current.contains(e.target);
            const isOnCell = !!e.target.closest('.ql-table-embed td, .ql-table-embed th');
            if (!isOnToolbar && !isOnCell) {
                setTableFocusState(prev => ({ ...prev, visible: false }));
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [tableFocusState.visible]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch]);

    useEffect(() => {
        if (tableFilters?.eventStatus) {
            localStorage.setItem(SUPPORT_TAB_STORAGE_KEY, tableFilters.eventStatus);
        }
    }, [tableFilters?.eventStatus]);

    useEffect(() => {
        fetchEvents();
    }, [pagination.page, pagination.limit, debouncedSearch, sortConfig]);

    const fetchEvents = async () => {
        setIsLoadingPage(true);
        try {
            const queryParams = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search: debouncedSearch
            });

            const eventStatus = tableFilters?.eventStatus;
            if (eventStatus === 'upcoming') {
                queryParams.append('status', 'UPCOMING');
            } else if (eventStatus === 'done') {
                queryParams.append('status', 'DONE');
            } else if (eventStatus === 'canceled') {
                queryParams.append('status', 'CANCELED');
            } else {
                queryParams.append('status', 'ALL');
            }

            const response = await fetch(`${config.EVENT_ENDPOINT}event?${queryParams.toString()}`);
            const result = await response.json();

            if (result.success) {
                setUpcomingEvent(result.meta?.stats?.upcomingEvent || 0);
                setDoneEvent(result.meta?.stats?.doneEvent || 0);
                setCanceledEvent(result.meta?.stats?.canceledEvent || 0);
                setAllEvent(result.meta?.stats?.allEvent || 0)
                setEvents(result.data);
                setPagination(prev => ({
                    ...prev,
                    total: result.meta?.totalItems || result.data.length,
                    totalPages: result.meta?.totalPages || Math.ceil(result.data.length / prev.limit)
                }));
            } else {
                console.error("Failed to fetch events");
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setIsLoadingPage(false);
        }
    };

    const clearSearch = () => {
        setTableSearch('');
    };

    const fallbackFormData = (eventData) => {
        setFormData({
            name: eventData.name || '',
            description: eventData.description || '',
            promptAi: eventData.promptAi || '',
            address: eventData.address || '',
            type: eventData.type,
            link: eventData.link || '',
            addressLink: eventData.addressLink || '',
            timeType: eventData.timeType || 'WIB',
            startDate: eventData.startDate ? format(new Date(eventData.startDate), 'yyyy-MM-dd\'T\'HH:mm') : '',
            endDate: eventData.endDate ? format(new Date(eventData.endDate), 'yyyy-MM-dd\'T\'HH:mm') : '',
            image: eventData.image || null,
            waitingSubject: eventData.waitingSubject || '',
            waitingText: eventData.waitingText || '',
            acceptSubject: eventData.acceptSubject || '',
            acceptText: eventData.acceptText || '',
            rejectSubject: eventData.rejectSubject || '',
            rejectText: eventData.rejectText || '',
            feedbackSubject: eventData.feedbackSubject || '',
            feedbackText: eventData.feedbackText || '',
            calenderText: eventData.calenderText || '',
            feedbackLink: eventData.feedbackLink || '',
            invitationSubject: eventData.invitationSubject || '',
            invitationText: eventData.invitationText || '',
            show: eventData.show || '',
            checkInManual: eventData.checkInManual || false,
            checkInQr: eventData.checkInQr || false,
            checkInFace: eventData.checkInFace || false,
            feedbackFormImage: eventData.feedbackFormImage || null,
            feedbackFormDescription: eventData.feedbackFormDescription || '',
            feedbackFormQuestions: parseFeedbackQuestions(eventData.feedbackFormQuestions),
            registrationFormDescription: eventData.registrationFormDescription || '',
            registrationFormQuestions: parseFeedbackQuestions(eventData.registrationFormQuestions)
        });
    };

    const handleOpenModal = async (eventData = null) => {
        setInitialData(eventData);
        if (eventData) {
            try {
                const response = await fetch(`${config.EVENT_ENDPOINT}event/${eventData.id}`);
                const result = await response.json();

                if (result.success && result.data) {
                    const fullData = result.data;
                    setFormData({
                        name: fullData.name || '',
                        description: fullData.description || '',
                        promptAi: fullData.promptAi || '',
                        address: fullData.address || '',
                        type: fullData.type || '',
                        link: fullData.link || '',
                        addressLink: fullData.addressLink || '',
                        timeType: fullData.timeType || '',
                        startDate: fullData.startDate ? format(new Date(fullData.startDate), 'yyyy-MM-dd\'T\'HH:mm') : '',
                        endDate: fullData.endDate ? format(new Date(fullData.endDate), 'yyyy-MM-dd\'T\'HH:mm') : '',
                        image: fullData.image || null,
                        waitingSubject: fullData.waitingSubject || '',
                        waitingText: fullData.waitingText || '',
                        acceptSubject: fullData.acceptSubject || '',
                        acceptText: fullData.acceptText || '',
                        rejectSubject: fullData.rejectSubject || '',
                        rejectText: fullData.rejectText || '',
                        feedbackSubject: fullData.feedbackSubject || '',
                        feedbackText: fullData.feedbackText || '',
                        calenderText: fullData.calenderText || '',
                        feedbackLink: fullData.feedbackLink || '',
                        invitationSubject: fullData.invitationSubject || '',
                        invitationText: fullData.invitationText || '',
                        show: fullData.show || '',
                        checkInManual: fullData.checkInManual || false,
                        checkInQr: fullData.checkInQr || false,
                        checkInFace: fullData.checkInFace || false,
                        feedbackFormImage: fullData.feedbackFormImage || null,
                        feedbackFormDescription: fullData.feedbackFormDescription || '',
                        feedbackFormQuestions: parseFeedbackQuestions(fullData.feedbackFormQuestions),
                        registrationFormDescription: fullData.registrationFormDescription || '',
                        registrationFormQuestions: parseFeedbackQuestions(fullData.registrationFormQuestions)
                    });
                    setFeedbackFormImagePreview(null);
                } else {
                    console.error("Failed to fetch event details");
                    fallbackFormData(eventData);
                }
            } catch (error) {
                console.error("Error fetching event details:", error);
                fallbackFormData(eventData);
            }
        } else {
            setFormData({
                name: '',
                description: '',
                promptAi: '',
                address: '',
                type: '',
                link: '',
                addressLink: '',
                timeType: '',
                startDate: '',
                endDate: '',
                image: null,
                waitingSubject: '',
                waitingText: '',
                acceptSubject: '',
                acceptText: '',
                rejectSubject: '',
                rejectText: '',
                feedbackSubject: '',
                feedbackText: '',
                calenderText: '',
                feedbackLink: '',
                invitationSubject: '',
                invitationText: '',
                show: '',
                checkInManual: false,
                checkInQr: false,
                checkInFace: false,
                feedbackFormImage: null,
                feedbackFormDescription: '',
                feedbackFormQuestions: [],
                registrationFormDescription: '',
                registrationFormQuestions: []
            });
            setFeedbackFormImagePreview(null);
        }
        setActiveTab('general');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowCancelConfirm(true);
    };

    const confirmCloseModal = () => {
        setShowCancelConfirm(false);
        setShowModal(false);
        setImagePreview(null);
        setFormData({
            name: '',
            description: '',
            promptAi: '',
            address: '',
            type: 'ON_SITE',
            link: '',
            addressLink: '',
            timeType: 'WIB',
            startDate: '',
            endDate: '',
            image: null,
            waitingSubject: '',
            waitingText: '',
            acceptSubject: '',
            acceptText: '',
            rejectSubject: '',
            rejectText: '',
            feedbackSubject: '',
            feedbackText: '',
            calenderText: '',
            feedbackLink: '',
            invitationSubject: '',
            invitationText: '',
            checkInManual: false,
            checkInQr: false,
            checkInFace: false,
            feedbackFormImage: null,
            feedbackFormDescription: '',
            feedbackFormQuestions: [],
            registrationFormDescription: '',
            registrationFormQuestions: []
        });
        setFeedbackFormImagePreview(null);
    };

    const cancelCloseModal = () => {
        setShowCancelConfirm(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const newFormData = { ...prev, [name]: value };

            // Date validation logic
            if (name === 'startDate' || name === 'endDate') {
                const start = name === 'startDate' ? value : newFormData.startDate;
                const end = name === 'endDate' ? value : newFormData.endDate;

                if (start && end) {
                    if (new Date(start) > new Date(end)) {
                        setDateError('Start Date cannot be later than End Date.');
                    } else {
                        setDateError('');
                    }
                } else {
                    setDateError('');
                }
            }

            return newFormData;
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert("File size exceeds 5MB limit. Please upload a smaller image.");
                e.target.value = '';
                return;
            }

            const img = new Image();
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                const ratio = width / height;

                if (width <= height) {
                    alert("Image width must be greater than its height. Recommended size: 1200x630 pixels.");
                    e.target.value = '';
                    return;
                }

                processImageFile(file);
            };
            img.src = URL.createObjectURL(file);
        } else {
            setFormData(prev => ({ ...prev, image: null }));
            setImagePreview(null);
        }
    };

    const processImageFile = (file) => {
        // Generate slug format from original file name
        const originalName = file.name;
        const extension = originalName.substring(originalName.lastIndexOf('.'));
        const baseName = originalName.substring(0, originalName.lastIndexOf('.'));

        const slugBase = baseName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)+/g, '');

        const newFileName = `${slugBase}${extension}`;

        const newFile = new File([file], newFileName, { type: file.type });

        setFormData(prev => ({ ...prev, image: newFile }));
        setImagePreview(URL.createObjectURL(newFile));
    };

    const handleQuillChange = (content, name) => {
        setFormData(prev => ({ ...prev, [name]: content }));
    };

    const parseFeedbackQuestions = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const makeFeedbackQuestion = (type) => {
        const base = {
            id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            type,
            question: '',
            required: false
        };
        if (type === 'radio' || type === 'checkbox') {
            return { ...base, options: [] };
        }
        if (type === 'linear_scale') {
            return { ...base, scaleMin: 1, scaleMax: 5, scaleMinLabel: '', scaleMaxLabel: '' };
        }
        return base;
    };

    const addFeedbackQuestion = (type) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: [...(prev.feedbackFormQuestions || []), makeFeedbackQuestion(type)]
        }));
    };

    const removeFeedbackQuestion = (qid) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: (prev.feedbackFormQuestions || []).filter(q => q.id !== qid)
        }));
    };

    const updateFeedbackQuestion = (qid, field, value) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: (prev.feedbackFormQuestions || []).map(q =>
                q.id === qid ? { ...q, [field]: value } : q
            )
        }));
    };

    const addFeedbackQuestionOption = (qid) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: (prev.feedbackFormQuestions || []).map(q =>
                q.id === qid
                    ? { ...q, options: [...(q.options || []), ''] }
                    : q
            )
        }));
    };

    const updateFeedbackQuestionOption = (qid, optIdx, value) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: (prev.feedbackFormQuestions || []).map(q =>
                q.id === qid
                    ? { ...q, options: q.options.map((opt, i) => i === optIdx ? value : opt) }
                    : q
            )
        }));
    };

    const removeFeedbackQuestionOption = (qid, optIdx) => {
        setFormData(prev => ({
            ...prev,
            feedbackFormQuestions: (prev.feedbackFormQuestions || []).map(q =>
                q.id === qid
                    ? { ...q, options: q.options.filter((_, i) => i !== optIdx) }
                    : q
            )
        }));
    };

    const addRegistrationQuestion = (type) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: [...(prev.registrationFormQuestions || []), makeFeedbackQuestion(type)]
        }));
    };
    const removeRegistrationQuestion = (qid) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: (prev.registrationFormQuestions || []).filter(q => q.id !== qid)
        }));
    };
    const updateRegistrationQuestion = (qid, field, value) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: (prev.registrationFormQuestions || []).map(q =>
                q.id === qid ? { ...q, [field]: value } : q
            )
        }));
    };
    const addRegistrationQuestionOption = (qid) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: (prev.registrationFormQuestions || []).map(q =>
                q.id === qid ? { ...q, options: [...(q.options || []), ''] } : q
            )
        }));
    };
    const updateRegistrationQuestionOption = (qid, optIdx, value) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: (prev.registrationFormQuestions || []).map(q =>
                q.id === qid ? { ...q, options: q.options.map((opt, i) => i === optIdx ? value : opt) } : q
            )
        }));
    };
    const removeRegistrationQuestionOption = (qid, optIdx) => {
        setFormData(prev => ({
            ...prev,
            registrationFormQuestions: (prev.registrationFormQuestions || []).map(q =>
                q.id === qid ? { ...q, options: q.options.filter((_, i) => i !== optIdx) } : q
            )
        }));
    };

    const handleFeedbackFormImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setFormData(prev => ({ ...prev, feedbackFormImage: null }));
            setFeedbackFormImagePreview(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert("File size exceeds 5MB limit. Please upload a smaller image.");
            e.target.value = '';
            return;
        }
        const originalName = file.name;
        const extension = originalName.substring(originalName.lastIndexOf('.'));
        const baseName = originalName.substring(0, originalName.lastIndexOf('.'));
        const slugBase = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
        const newFile = new File([file], `${slugBase}${extension}`, { type: file.type });
        setFormData(prev => ({ ...prev, feedbackFormImage: newFile }));
        setFeedbackFormImagePreview(URL.createObjectURL(newFile));
    };

    const deleteEvent = async (id) => {
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}event/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data.success || response.ok) {
                fetchEvents();
            }
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent submission if there's a date error
        if (dateError) {
            alert("Please resolve the date error before submitting.");
            return;
        }

        if (!formData.acceptSubject || !formData.acceptText || !formData.rejectSubject || !formData.rejectText || !formData.feedbackSubject || !formData.feedbackText) {
            alert("Please fill in all required fields in the Email Templates tab (Accept, Reject, and Feedback templates).");
            setActiveTab('templates');
            return;
        }

        setIsSaving(true);

        try {
            let payload = {
                name: formData.name,
                description: formData.description,
                promptAi: formData.promptAi,
                type: formData.type,
                address: formData.address,
                addressLink: formData.addressLink,
                timeType: formData.timeType,
                startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
                endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
                waitingSubject: formData.waitingSubject,
                waitingText: formData.waitingText,
                acceptSubject: formData.acceptSubject,
                acceptText: formData.acceptText,
                rejectSubject: formData.rejectSubject,
                rejectText: formData.rejectText,
                feedbackSubject: formData.feedbackSubject,
                show: formData.show,
                feedbackText: formData.feedbackText,
                invitationSubject: formData.invitationSubject,
                invitationText: formData.invitationText,
                feedbackLink: formData.feedbackLink,
                link: formData.link,
                calenderText: formData.calenderText,
                checkInManual: formData.checkInManual,
                checkInQr: formData.checkInQr,
                checkInFace: formData.checkInFace,
                feedbackFormDescription: formData.feedbackFormDescription,
                feedbackFormQuestions: JSON.stringify(formData.feedbackFormQuestions || []),
                registrationFormDescription: formData.registrationFormDescription,
                registrationFormQuestions: JSON.stringify(formData.registrationFormQuestions || []),
            };

            Object.keys(payload).forEach(key => {
                if (payload[key] === '') delete payload[key];
            });

            const isEdit = !!initialData?.id;
            const url = isEdit
                ? `${config.EVENT_ENDPOINT}event/${initialData.id}`
                : `${config.EVENT_ENDPOINT}event`;

            const method = isEdit ? 'PATCH' : 'POST';

            let fetchOptions = {
                method: method,
            };

            const formPayload = new FormData();
            Object.keys(payload).forEach(key => {
                if (payload[key] !== null && payload[key] !== undefined) {
                    formPayload.append(key, typeof payload[key] === 'boolean' ? String(payload[key]) : payload[key]);
                }
            });

            if (formData.image && typeof formData.image !== 'string') {
                formPayload.append('image', formData.image);
            }

            if (formData.feedbackFormImage && typeof formData.feedbackFormImage !== 'string') {
                formPayload.append('feedbackFormImage', formData.feedbackFormImage);
            }

            fetchOptions.body = formPayload;
            const response = await jwtFetch(url, fetchOptions);

            const result = await response.json();

            if (result.success || response.ok) {
                alert(`Event successfully ${isEdit ? 'updated' : 'created'}!`);
                setShowModal(false);
                fetchEvents();
            } else {
                alert(`Error: ${result.message || 'Failed to save event'}`);
                console.error("API Error:", result);
            }
        } catch (error) {
            console.error("Error saving event:", error);
            alert("An error occurred while saving the event.");
        } finally {
            setIsSaving(false);
        }
    };

    const renderPaginationItems = () => {
        const { page, totalPages } = pagination;
        const items = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (page >= totalPages - 3) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                items.push(page - 1);
                items.push(page);
                items.push(page + 1);
                items.push('...');
                items.push(totalPages);
            }
        }

        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${pagination.page === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => setPagination(prev => ({ ...prev, page: item }))}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    return (
        <div id="event-list">
            <style>{`
                .ql-table-embed { display: block; margin: 6px 0; }
                .ql-table-embed table { border-collapse: collapse; width: 100%; }
                .ql-table-embed td, .ql-table-embed th { border: 1px solid #dee2e6; padding: 6px 10px; min-width: 80px; }
                .ql-table-embed th { background: #f8f9fa; font-weight: 600; }
                .ql-table-embed td:focus, .ql-table-embed th:focus { outline: 2px solid #0d6efd; outline-offset: -2px; }
                .ql-table-cell-toolbar button { line-height: 1; }
                .ql-table-cell-toolbar .sep { width: 1px; background: #dee2e6; margin: 2px 2px; align-self: stretch; }
            `}</style>

            {tablePickerOpen && (
                <div style={{ position: 'fixed', top: tablePickerPos.top, left: tablePickerPos.left, zIndex: 9999 }}>
                    <TablePicker onSelect={handleInsertTable} />
                </div>
            )}

            {tableFocusState.visible && (
                <div
                    ref={tableFocusRef}
                    className="ql-table-cell-toolbar"
                    onMouseDown={e => e.preventDefault()}
                    style={{
                        position: 'fixed',
                        top: tableFocusState.top,
                        left: tableFocusState.left,
                        zIndex: 10000,
                        background: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        padding: '3px 4px',
                    }}
                >
                    {[
                        { title: 'Insert row above', icon: 'bi-arrow-bar-up', action: insertRowAbove },
                        { title: 'Insert row below', icon: 'bi-arrow-bar-down', action: insertRowBelow },
                    ].map(({ title, icon, action }) => (
                        <button key={title} title={title} onClick={action}
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: '2px 6px', fontSize: '13px', border: 'none' }}>
                            <i className={`bi ${icon}`} />
                        </button>
                    ))}
                    <div className="sep" />
                    {[
                        { title: 'Insert column left', icon: 'bi-arrow-bar-left', action: insertColLeft },
                        { title: 'Insert column right', icon: 'bi-arrow-bar-right', action: insertColRight },
                    ].map(({ title, icon, action }) => (
                        <button key={title} title={title} onClick={action}
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: '2px 6px', fontSize: '13px', border: 'none' }}>
                            <i className={`bi ${icon}`} />
                        </button>
                    ))}
                    <div className="sep" />
                    {[
                        { title: 'Delete row', icon: 'bi-dash-square', action: deleteRow },
                        { title: 'Delete column', icon: 'bi-x-square', action: deleteCol },
                    ].map(({ title, icon, action }) => (
                        <button key={title} title={title} onClick={action}
                            className="btn btn-sm btn-outline-danger"
                            style={{ padding: '2px 6px', fontSize: '13px', border: 'none' }}>
                            <i className={`bi ${icon}`} />
                        </button>
                    ))}
                </div>
            )}

            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0" />
                        <Menu active="event" />
                    </div>
                    <div className="col-md-10">
                        <Header />
                        <hr className="m-0" />
                        <div className="main">
                            <div className="p-3 p-md-4">
                                <section className="mb-4">
                                    <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-4">
                                            <div className="fs-24">Event</div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="input-group input-search">
                                                <button className="btn bg-white" type="button" onClick={() => clearSearch()}>
                                                    {tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}
                                                </button>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder="Search"
                                                    value={tableSearch}
                                                    onChange={(e) => setTableSearch(e.target.value)}
                                                />
                                            </div>
                                            {((crmUser.user_department !== "Sales" || crmUser.user_level === 'Admin') && crmUser.user_level !== 'View') && (
                                                <button className="btn btn-primary bg-gold text-dark border-0" type="button" onClick={() => handleOpenModal()}>
                                                    <i className="bi bi-plus-circle me-1" /> Add
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-white border-radius-4 p-4">
                                        <Tabs
                                            className="mb-3"
                                            activeKey={tableFilters?.eventStatus || "Upcoming"}
                                            onSelect={(key) => {
                                                setTableFilters({ eventStatus: key });
                                                setPagination(prev => ({ ...prev, page: 1 }));
                                                setSortConfig({ key: 'startDate', direction: 'asc' });
                                            }}
                                        >
                                            <Tab eventKey="all" title={<OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>List of All Events</Tooltip>}
                                            >
                                                <span>All Events ({allEvent})</span>
                                            </OverlayTrigger>} />
                                            <Tab eventKey="upcoming" title={<OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>List of Upcoming Events</Tooltip>}
                                            >
                                                <span>Upcoming Events ({upcomingEvent})</span>
                                            </OverlayTrigger>} />
                                            <Tab eventKey="done" title={<OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>List of Completed Events</Tooltip>}
                                            >
                                                <span>Completed Events ({doneEvent})</span>
                                            </OverlayTrigger>} />
                                            <Tab eventKey="canceled" title={<OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>List of Deleted Events</Tooltip>}
                                            >
                                                <span>Canceled Events ({canceledEvent})</span>
                                            </OverlayTrigger>} />
                                        </Tabs>
                                        {isLoadingPage ? (
                                            <p>Loading...</p>
                                        ) : (
                                            events && events.length > 0 ? (
                                                <>
                                                    <div className="table-responsive">
                                                        <table className="table table-hover">
                                                            <thead>
                                                                <tr>
                                                                    <th className="bg-gold fw-bold border-0 text-start" style={{ borderTopLeftRadius: '8px' }}>#</th>
                                                                    <th className="bg-gold fw-bold border-0 text-start">Event Name</th>
                                                                    <th className="bg-gold fw-bold border-0 text-start">Type</th>
                                                                    <th className="bg-gold fw-bold border-0 text-start">Date</th>
                                                                    <th className="bg-gold fw-bold border-0 text-start">Location</th>
                                                                    <th className="bg-gold fw-bold border-0 text-start">Status</th>
                                                                    {((crmUser.user_department !== 'Sales' || crmUser.user_level === 'Admin') && crmUser.user_level !== 'View') && (
                                                                        <th className="bg-gold fw-bold border-0 text-start" style={{ borderTopRightRadius: '8px' }}>Actions</th>
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {events.map((item, index) => (
                                                                    <tr
                                                                        key={item.id}
                                                                        onClick={() => navigate(`/event/detail/${item.id}`)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <td className="text-start">{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                                                                        <td>{item.name}</td>
                                                                        <td className="text-start">{item.type.replace('_', ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase())}</td>
                                                                        <td className="text-start">{format(new Date(item.startDate), 'd MMM yyyy HH:mm')} {item.timeType}</td>
                                                                        <td>{item.address}</td>
                                                                        <td className="text-start">
                                                                            <span className={`badge ${item.status === 'UPCOMING' ? 'bg-warning text-dark' : item.status === 'ON_PROGRESS' ? 'bg-primary' : item.status === 'DONE' ? 'bg-success' : 'bg-danger'} px-3 py-2 rounded`}>
                                                                                {item.status === 'UPCOMING' ? 'Upcoming' : item.status === 'ON_PROGRESS' ? 'Ongoing' : item.status === 'DONE' ? 'Done' : 'Canceled'}
                                                                            </span>
                                                                        </td>
                                                                        {((crmUser.user_department !== 'Sales' || crmUser.user_level === 'Admin') && crmUser.user_level !== 'View') && (
                                                                            <td className=" text-start" onClick={(e) => e.stopPropagation()}>
                                                                                <Dropdown drop="down-centered">
                                                                                    <Dropdown.Toggle variant="light" size="sm" className="border-0 bg-transparent text-dark shadow-none p-0" id={`dropdown-${item.id}`}>
                                                                                        <i className="bi bi-three-dots-vertical fs-5"></i>
                                                                                    </Dropdown.Toggle>

                                                                                    <Dropdown.Menu className="shadow-sm border-0 z-3" renderOnMount popperConfig={{ strategy: 'fixed', modifiers: [{ name: 'preventOverflow', options: { rootBoundary: 'viewport' } }] }}>
                                                                                        <Dropdown.Item onClick={() => handleOpenModal(item)}>
                                                                                            <i className="bi bi-pencil-square me-2 text-warning"></i> Edit
                                                                                        </Dropdown.Item>
                                                                                        <Dropdown.Item onClick={() => navigate(`/event/invitation/${item.id}`)}>
                                                                                            <i className="bi bi-envelope-fill me-2 text-primary"></i> Send Invitation
                                                                                        </Dropdown.Item>
                                                                                        <Dropdown.Item onClick={() => {
                                                                                            const link = `${config.EVENT_BASEURL}/events/register/${item.slug}`;
                                                                                            navigator.clipboard.writeText(link);
                                                                                            alert('Registration Link Copied: ' + link);
                                                                                        }}>
                                                                                            <i className="bi bi-link-45deg me-2 text-secondary"></i> Registration Link
                                                                                        </Dropdown.Item>
                                                                                        <Dropdown.Item onClick={() => window.open(`${config.AI_CHECKIN_BASEURL}/${item.slug}/check-in`, '_blank')}>
                                                                                            <i class="bi bi-clipboard-check-fill me-2 text-success"></i> Check-In Link
                                                                                        </Dropdown.Item>
                                                                                        <Dropdown.Item onClick={() => window.open(`${config.EVENT_BASEURL}/${item.slug}/feedback`, '_blank')}>
                                                                                            <i class="bi bi-chat-left-text-fill me-2 text-dark"></i> Feedback Link
                                                                                        </Dropdown.Item>
                                                                                        <Dropdown.Divider />
                                                                                        <Dropdown.Item onClick={() => {
                                                                                            if (window.confirm('Are you sure you want to delete this event?')) {
                                                                                                deleteEvent(item.id);
                                                                                            }
                                                                                        }} className="text-danger">
                                                                                            <i className="bi bi-trash-fill me-2"></i> Delete
                                                                                        </Dropdown.Item>
                                                                                    </Dropdown.Menu>
                                                                                </Dropdown>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {pagination.totalPages > 0 && (
                                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                                            <div className="d-flex align-items-center gap-2 text-secondary">
                                                                <small>Show</small>
                                                                <select
                                                                    className="form-select form-select-sm border-secondary text-secondary"
                                                                    style={{ width: '70px', borderRadius: '4px' }}
                                                                    value={pagination.limit}
                                                                    onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                                                                >
                                                                    <option value={10}>10</option>
                                                                    <option value={20}>20</option>
                                                                    <option value={50}>50</option>
                                                                </select>
                                                                <small>entries</small>
                                                            </div>
                                                            <nav>
                                                                <ul className="pagination pagination-sm mb-0 gap-1">
                                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                                                                            <i className="bi bi-chevron-left" />
                                                                        </button>
                                                                    </li>
                                                                    {renderPaginationItems()}
                                                                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                                                            <i className="bi bi-chevron-right" />
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </nav>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="text-center p-4">No data available</div>
                                            )
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Create/Edit Event */}
            <Modal show={showModal} onHide={handleCloseModal} size="xl" centered scrollable backdrop="static">
                <Modal.Header closeButton className="border-bottom pb-3">
                    <Modal.Title className="fs-5 fw-bold text-dark">
                        {initialData ? 'Edit Event' : 'Create New Event'}
                    </Modal.Title>
                </Modal.Header>

                <div
                    style={{
                        display: 'flex',
                        flexShrink: 0,
                        flexWrap: 'nowrap',
                        alignItems: 'stretch',
                        borderBottom: '1px solid #dee2e6',
                        padding: '0 1rem',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        minHeight: 48,
                        background: '#fff'
                    }}
                >
                    {[
                        { key: 'general', label: 'General Information' },
                        { key: 'templates', label: 'Email Templates' },
                        { key: 'feedbackForm', label: 'Feedback Form' },
                        { key: 'registrationForm', label: 'Registration Form' }
                    ].map(({ key, label }) => {
                        const isActive = activeTab === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setActiveTab(key)}
                                style={{
                                    flex: '0 0 auto',
                                    padding: '12px 20px',
                                    border: 'none',
                                    borderBottom: isActive ? '2px solid #ffc107' : '2px solid transparent',
                                    background: 'transparent',
                                    color: isActive ? '#212529' : '#6c757d',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    lineHeight: 1.4,
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    borderRadius: 0,
                                    minWidth: 'auto'
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                <Modal.Body className="p-4 bg-white" style={{ minHeight: '400px' }}>
                    <form id="event-form" onSubmit={handleSubmit}>
                        {activeTab === 'general' && (
                            <div className="row g-4">
                                <div className="col-md-12">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Event Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="form-control p-2"
                                        placeholder="Enter event name"
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <ReactQuill
                                        theme="snow"
                                        placeholder="Provide detailed information about the event"
                                        required
                                        value={formData.description}
                                        onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        style={{ height: '400px', marginBottom: '55px' }}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Prompt AI <span className="text-danger ms-1">*</span>
                                        <span className="ms-2 badge bg-light text-primary border border-primary" onClick={() => setShowPromptExample(true)} style={{ cursor: 'pointer' }}>
                                            <i className="bi bi-info-circle me-1"></i> View Example
                                        </span>
                                    </label>
                                    <textarea
                                        required
                                        name="promptAi"
                                        value={formData.promptAi}
                                        onChange={handleChange}
                                        placeholder="Enter AI prompt..."
                                        className="form-control p-2 font-monospace"
                                        rows="5"
                                        style={{ height: '400px', backgroundColor: '#f8f9fa' }}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Event Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                        required
                                        className="form-select p-2"
                                    >
                                        <option value="">Select Event Type</option>
                                        <option value="ON_SITE">Offline</option>
                                        <option value="ONLINE">Online</option>
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Event Visibility <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="show"
                                        value={formData.show}
                                        onChange={handleChange}
                                        required
                                        className="form-select p-2"
                                    >
                                        <option value="">Select Event Visibility</option>
                                        <option value="PUBLIC">Public</option>
                                        <option value="PRIVATE">Private</option>
                                    </select>
                                </div>

                                <div className="col-12">
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center gap-2">
                                        Check-In Type
                                        {initialData && (
                                            <span className="badge bg-secondary fw-normal" style={{ fontSize: '0.7rem' }}>Read only</span>
                                        )}
                                    </label>
                                    <div className="d-flex gap-4" style={initialData ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="checkInManual"
                                                name="checkInManual"
                                                checked={formData.checkInManual}
                                                onChange={initialData ? () => {} : handleChange}
                                                disabled={!!initialData}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInManual">
                                                Manual
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            The event admin manually searches for the participant's registration and marks them as attended upon arrival.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i className="bi bi-info-circle text-secondary" style={{ fontSize: '0.8rem', cursor: 'pointer' }}></i>
                                                </OverlayTrigger>
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="checkInQr"
                                                name="checkInQr"
                                                checked={formData.checkInQr}
                                                onChange={initialData ? () => {} : handleChange}
                                                disabled={!!initialData}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInQr">
                                                QR Scan
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Approved participants receive a unique QR code via email. They present it at the venue to be scanned for instant check-in.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i className="bi bi-info-circle text-secondary" style={{ fontSize: '0.8rem', cursor: 'pointer' }}></i>
                                                </OverlayTrigger>
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="checkInFace"
                                                name="checkInFace"
                                                checked={formData.checkInFace}
                                                onChange={initialData ? () => {} : handleChange}
                                                disabled={!!initialData}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInFace">
                                                Face Recognition
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Participants register their face in advance. On event day, they simply look at the camera to check in automatically — no QR code or admin needed.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i className="bi bi-info-circle text-secondary" style={{ fontSize: '0.8rem', cursor: 'pointer' }}></i>
                                                </OverlayTrigger>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Address / Location <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        required
                                        placeholder="Location or Address"
                                        className="form-control p-2"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Link Address <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        name="addressLink"
                                        value={formData.addressLink}
                                        onChange={handleChange}
                                        placeholder="e.g. https://maps.google.com/..."
                                        className="form-control p-2"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label text-secondary fw-semibold mb-2">Event Link (Online Only)</label>
                                    <input
                                        type="text"
                                        name="link"
                                        disabled={formData.type === 'ON_SITE'}
                                        value={formData.link}
                                        onChange={handleChange}
                                        className="form-control p-2"
                                        placeholder="Enter event URL"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Start Date & Time <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <input
                                            type="datetime-local"
                                            name="startDate"
                                            value={formData.startDate}
                                            onChange={handleChange}
                                            required
                                            style={dateError ? { paddingRight: '2rem', backgroundImage: 'none' } : {}}
                                            className={`form-control p-2 ${dateError ? 'is-invalid' : ''}`}
                                        />
                                        {dateError && <div className="invalid-feedback">{dateError}</div>}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        End Date & Time <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <input
                                            type="datetime-local"
                                            name="endDate"
                                            value={formData.endDate}
                                            onChange={handleChange}
                                            required
                                            style={dateError ? { paddingRight: '2rem', backgroundImage: 'none' } : {}}
                                            className={`form-control p-2 ${dateError ? 'is-invalid' : ''}`}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Time Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="timeType"
                                        value={formData.timeType}
                                        onChange={handleChange}
                                        required
                                        className="form-select p-2"
                                    >
                                        <option value="">Select Time Type</option>
                                        <option value="WIB">WIB (Western Indonesia Time)</option>
                                        <option value="WITA">WITA (Central Indonesia Time)</option>
                                        <option value="WIT">WIT (Eastern Indonesia Time)</option>
                                    </select>
                                </div>

                                <div className="col-12">
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Upload Thumbnail Image <span className="text-danger">*</span>
                                    </label>
                                    <div className="text-secondary small mb-2 lh-sm">
                                        <i className="bi bi-info-circle me-1"></i>
                                        <strong>Recommended Size: 1200 x 630 px (Aspect Ratio 16:9 or 1.9:1)</strong>  <br />
                                        <i className="bi bi-info-circle me-1 text-transparent"></i>
                                        <strong>Maximum Image Size:</strong> 5MB.
                                    </div>
                                    <input
                                        type="file"
                                        name="image"
                                        onChange={handleImageChange}
                                        className="form-control p-2"
                                        accept="image/*"
                                    />

                                    {imagePreview ? (
                                        <div className="mt-3">
                                            <p className="mb-1 text-secondary small">Preview ({formData.image?.name}):</p>
                                            <img
                                                src={imagePreview}
                                                alt="Banner preview"
                                                className="img-thumbnail"
                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : formData.image && typeof formData.image === 'string' ? (
                                        <div className="mt-3">
                                            <p className="mb-1 text-secondary small">Current Image:</p>
                                            <img
                                                src={config.EVENT_ENDPOINT + 'event/image/' + formData.image}
                                                alt="Current banner"
                                                className="img-thumbnail"
                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {activeTab === 'templates' && (
                            <div className="d-flex flex-column gap-4">
                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Email Invitation Template <span className="text-danger">*</span>
                                        <TooltipInfo text="Template message for invitation participants" />
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="invitationSubject"
                                        value={formData.invitationSubject}
                                        onChange={handleChange}
                                        className="form-control p-2 mb-3"
                                        placeholder="Invitation Subject"
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.invitationText}
                                        onChange={(content) => handleQuillChange(content, 'invitationText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Invitation Text..."
                                        style={{ height: '280px', marginBottom: '55px' }}
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Registration Confirmation Template <span className="text-danger">*</span>
                                        <TooltipInfo text="Template message for waiting participants" />
                                    </label>
                                    <input
                                        type="text"
                                        name="waitingSubject"
                                        value={formData.waitingSubject}
                                        onChange={handleChange}
                                        className="form-control p-2 mb-3"
                                        placeholder="Registration Confirmation Subject"
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.waitingText}
                                        onChange={(content) => handleQuillChange(content, 'waitingText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Registration Confirmation Text..."
                                        style={{ height: '280px', marginBottom: '55px' }}
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Approval Confirmation Template <span className="text-danger">*</span>
                                        <TooltipInfo text="Template message for accepted participants in the event" />
                                    </label>
                                    <input
                                        type="text"
                                        name="acceptSubject"
                                        value={formData.acceptSubject}
                                        onChange={handleChange}
                                        className="form-control p-2 mb-3"
                                        placeholder="Approval Confirmation Subject"
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.acceptText}
                                        onChange={(content) => handleQuillChange(content, 'acceptText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Approval Confirmation Text..."
                                        style={{ height: '280px', marginBottom: '55px' }}
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Rejection Confirmation Template <span className="text-danger">*</span>
                                        <TooltipInfo text="Template message for rejected participants in the event" />
                                    </label>
                                    <input
                                        type="text"
                                        name="rejectSubject"
                                        value={formData.rejectSubject}
                                        onChange={handleChange}
                                        className="form-control p-2 mb-3"
                                        placeholder="Rejection Confirmation Subject"
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.rejectText}
                                        onChange={(content) => handleQuillChange(content, 'rejectText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Rejection Confirmation Text..."
                                        style={{ height: '280px', marginBottom: '55px' }}
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Post-Event Template <span className="text-danger">*</span>
                                        <TooltipInfo text="Template message for feedback invitation" />
                                    </label>
                                    <input
                                        type="text"
                                        name="feedbackSubject"
                                        value={formData.feedbackSubject}
                                        onChange={handleChange}
                                        className="form-control p-2 mb-3"
                                        placeholder="Post-Event Subject"
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.feedbackText}
                                        onChange={(content) => handleQuillChange(content, 'feedbackText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Post-Event Text..."
                                        style={{ height: '280px', marginBottom: '55px' }}
                                    />
                                </div>

                                {/* <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Calendar Text
                                        <TooltipInfo text="Template message for event invitation calendar" />
                                    </label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.calenderText}
                                        onChange={(content) => handleQuillChange(content, 'calenderText')}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="bg-white"
                                        placeholder="Calendar Text..."
                                    />
                                </div> */}
                            </div>
                        )}

                        {activeTab === 'feedbackForm' && (
                            <div className="d-flex flex-column gap-4">
                                <div className="text-secondary small">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Design the feedback form that participants will fill out after the event. The fixed fields below are always collected. Add custom questions as needed.
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Header Image
                                    </label>
                                    <div className="text-secondary small mb-2 lh-sm">
                                        <i className="bi bi-info-circle me-1"></i>
                                        <strong>Recommended Size: 1200 x 400 px.</strong> <strong>Max Size:</strong> 5MB.
                                    </div>
                                    <input
                                        type="file"
                                        name="feedbackFormImage"
                                        onChange={handleFeedbackFormImageChange}
                                        className="form-control p-2"
                                        accept="image/*"
                                    />
                                    {feedbackFormImagePreview ? (
                                        <div className="mt-3">
                                            <p className="mb-1 text-secondary small">Preview ({formData.feedbackFormImage?.name}):</p>
                                            <img
                                                src={feedbackFormImagePreview}
                                                alt="Feedback header preview"
                                                className="img-thumbnail"
                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : formData.feedbackFormImage && typeof formData.feedbackFormImage === 'string' ? (
                                        <div className="mt-3">
                                            <p className="mb-1 text-secondary small">Current Image:</p>
                                            <img
                                                src={config.EVENT_ENDPOINT + 'event/image/' + formData.feedbackFormImage}
                                                alt="Current feedback header"
                                                className="img-thumbnail"
                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Feedback Page Description
                                    </label>
                                    <textarea
                                        name="feedbackFormDescription"
                                        value={formData.feedbackFormDescription}
                                        onChange={handleChange}
                                        className="form-control p-2"
                                        rows="4"
                                        placeholder="Describe the purpose of this feedback form (shown at the top of the page)."
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Fixed Fields
                                        <TooltipInfo text="These fields are always collected from every participant and cannot be removed." />
                                    </label>
                                    <div className="border rounded-3 p-3 bg-light">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Nama</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Participant's name" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Alamat Email <span className="text-danger">*</span></label>
                                                <input type="email" disabled className="form-control form-control-sm bg-white" placeholder="participant@email.com" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">No Handphone <span className="text-danger">*</span></label>
                                                <input type="tel" disabled className="form-control form-control-sm bg-white" placeholder="+62..." />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Nama Perusahaan <span className="text-danger">*</span></label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Company name" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Struktur Organisasi Level <span className="text-danger">*</span></label>
                                                <select disabled className="form-select form-select-sm bg-white">
                                                    <option>C-Level</option>
                                                    <option>Manager-Level</option>
                                                    <option>Supervisor-Level</option>
                                                    <option>Staff-Level</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Posisi / Jabatan</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Job title" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label text-secondary fw-semibold mb-0 d-flex align-items-center">
                                            Custom Questions
                                            <TooltipInfo text="Add custom questions to collect additional feedback. Supports short text, radio, checkbox, and linear scale." />
                                        </label>
                                        <Dropdown>
                                            <Dropdown.Toggle className="btn btn-sm bg-gold text-dark border-0 fw-semibold">
                                                <i className="bi bi-plus-lg me-1"></i>Add Question
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => addFeedbackQuestion('short_text')}>
                                                    <i className="bi bi-text-left me-2"></i>Short Text
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addFeedbackQuestion('radio')}>
                                                    <i className="bi bi-ui-radios me-2"></i>Multiple Choice
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addFeedbackQuestion('checkbox')}>
                                                    <i className="bi bi-ui-checks me-2"></i>Checkboxes
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addFeedbackQuestion('linear_scale')}>
                                                    <i className="bi bi-sliders me-2"></i>Linear Scale
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    {(formData.feedbackFormQuestions || []).length === 0 ? (
                                        <div className="border border-dashed rounded-3 p-4 text-center text-secondary small">
                                            No custom questions yet. Click "Add Question" to create one.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {(formData.feedbackFormQuestions || []).map((q, idx) => (
                                                <div key={q.id} className="border rounded-3 p-3 bg-white">
                                                    <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                                        <span className="badge bg-light text-secondary border">
                                                            Q{idx + 1} ·{' '}
                                                            {q.type === 'short_text' && 'Short Text'}
                                                            {q.type === 'radio' && 'Multiple Choice'}
                                                            {q.type === 'checkbox' && 'Checkboxes'}
                                                            {q.type === 'linear_scale' && 'Linear Scale'}
                                                        </span>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="form-check form-switch m-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`required-${q.id}`}
                                                                    checked={!!q.required}
                                                                    onChange={(e) => updateFeedbackQuestion(q.id, 'required', e.target.checked)}
                                                                />
                                                                <label className="form-check-label small text-secondary" htmlFor={`required-${q.id}`}>Required</label>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger p-0"
                                                                onClick={() => removeFeedbackQuestion(q.id)}
                                                                title="Remove question"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <input
                                                        type="text"
                                                        value={q.question}
                                                        onChange={(e) => updateFeedbackQuestion(q.id, 'question', e.target.value)}
                                                        className="form-control p-2 mb-2"
                                                        placeholder="Enter your question"
                                                    />

                                                    {q.type === 'short_text' && (
                                                        <input type="text" disabled className="form-control form-control-sm bg-light" placeholder="Participant's answer" />
                                                    )}

                                                    {(q.type === 'radio' || q.type === 'checkbox') && (
                                                        <div className="d-flex flex-column gap-2">
                                                            {(q.options || []).map((opt, optIdx) => (
                                                                <div key={optIdx} className="d-flex align-items-center gap-2">
                                                                    <i className={`bi ${q.type === 'radio' ? 'bi-circle' : 'bi-square'} text-secondary`}></i>
                                                                    <input
                                                                        type="text"
                                                                        value={opt}
                                                                        onChange={(e) => updateFeedbackQuestionOption(q.id, optIdx, e.target.value)}
                                                                        className="form-control form-control-sm"
                                                                        placeholder={`Option ${optIdx + 1}`}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link text-danger p-0"
                                                                        onClick={() => removeFeedbackQuestionOption(q.id, optIdx)}
                                                                        disabled={(q.options || []).length <= 1}
                                                                        title="Remove option"
                                                                    >
                                                                        <i className="bi bi-x-lg"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-decoration-none align-self-start p-0"
                                                                onClick={() => addFeedbackQuestionOption(q.id)}
                                                            >
                                                                <i className="bi bi-plus-lg me-1"></i>Add option
                                                            </button>
                                                        </div>
                                                    )}

                                                    {q.type === 'linear_scale' && (
                                                        <div className="row g-2 align-items-end">
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">From</label>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={q.scaleMin}
                                                                    onChange={(e) => updateFeedbackQuestion(q.id, 'scaleMin', Number(e.target.value))}
                                                                >
                                                                    <option value={0}>0</option>
                                                                    <option value={1}>1</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">To</label>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={q.scaleMax}
                                                                    onChange={(e) => updateFeedbackQuestion(q.id, 'scaleMax', Number(e.target.value))}
                                                                >
                                                                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                                                        <option key={n} value={n}>{n}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">{q.scaleMin} label</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={q.scaleMinLabel || ''}
                                                                    onChange={(e) => updateFeedbackQuestion(q.id, 'scaleMinLabel', e.target.value)}
                                                                    placeholder="e.g. Poor"
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">{q.scaleMax} label</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={q.scaleMaxLabel || ''}
                                                                    onChange={(e) => updateFeedbackQuestion(q.id, 'scaleMaxLabel', e.target.value)}
                                                                    placeholder="e.g. Excellent"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'registrationForm' && (
                            <div className="d-flex flex-column gap-4">
                                <div className="text-secondary small">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Design additional questions that participants will fill out during registration. Fixed fields (name, email, phone, etc.) are always collected separately.
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2">
                                        Registration Page Description
                                    </label>
                                    <textarea
                                        name="registrationFormDescription"
                                        value={formData.registrationFormDescription}
                                        onChange={handleChange}
                                        className="form-control p-2"
                                        rows="4"
                                        placeholder="Describe additional information or instructions for the registration form."
                                    />
                                </div>

                                <div>
                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                        Fixed Fields
                                        <TooltipInfo text="These fields are always collected from every participant and cannot be removed." />
                                    </label>
                                    <div className="border rounded-3 p-3 bg-light">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Nama <span className="text-danger">*</span></label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Full name" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Gender / Sapaan</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Mr / Mrs / Ms" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">No Handphone <span className="text-danger">*</span></label>
                                                <input type="tel" disabled className="form-control form-control-sm bg-white" placeholder="+62..." />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Email</label>
                                                <input type="email" disabled className="form-control form-control-sm bg-white" placeholder="participant@email.com" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Perusahaan <span className="text-danger">*</span></label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Company name" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Posisi / Jabatan</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Job title" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Industri</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Industry" />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-secondary mb-1">Departemen</label>
                                                <input type="text" disabled className="form-control form-control-sm bg-white" placeholder="Department" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label text-secondary fw-semibold mb-0 d-flex align-items-center">
                                            Custom Questions
                                            <TooltipInfo text="Add custom questions to collect additional information during registration. Supports short text, radio, checkbox, and linear scale." />
                                        </label>
                                        <Dropdown>
                                            <Dropdown.Toggle className="btn btn-sm bg-gold text-dark border-0 fw-semibold">
                                                <i className="bi bi-plus-lg me-1"></i>Add Question
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => addRegistrationQuestion('short_text')}>
                                                    <i className="bi bi-text-left me-2"></i>Short Text
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addRegistrationQuestion('radio')}>
                                                    <i className="bi bi-ui-radios me-2"></i>Multiple Choice
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addRegistrationQuestion('checkbox')}>
                                                    <i className="bi bi-ui-checks me-2"></i>Checkboxes
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => addRegistrationQuestion('linear_scale')}>
                                                    <i className="bi bi-sliders me-2"></i>Linear Scale
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    {(formData.registrationFormQuestions || []).length === 0 ? (
                                        <div className="border border-dashed rounded-3 p-4 text-center text-secondary small">
                                            No custom questions yet. Click "Add Question" to create one.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {(formData.registrationFormQuestions || []).map((q, idx) => (
                                                <div key={q.id} className="border rounded-3 p-3 bg-white">
                                                    <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                                        <span className="badge bg-light text-secondary border">
                                                            Q{idx + 1} ·{' '}
                                                            {q.type === 'short_text' && 'Short Text'}
                                                            {q.type === 'radio' && 'Multiple Choice'}
                                                            {q.type === 'checkbox' && 'Checkboxes'}
                                                            {q.type === 'linear_scale' && 'Linear Scale'}
                                                        </span>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="form-check form-switch m-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`reg-required-${q.id}`}
                                                                    checked={!!q.required}
                                                                    onChange={(e) => updateRegistrationQuestion(q.id, 'required', e.target.checked)}
                                                                />
                                                                <label className="form-check-label small text-secondary" htmlFor={`reg-required-${q.id}`}>Required</label>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger p-0"
                                                                onClick={() => removeRegistrationQuestion(q.id)}
                                                                title="Remove question"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <input
                                                        type="text"
                                                        value={q.question}
                                                        onChange={(e) => updateRegistrationQuestion(q.id, 'question', e.target.value)}
                                                        className="form-control p-2 mb-2"
                                                        placeholder="Enter your question"
                                                    />

                                                    {q.type === 'short_text' && (
                                                        <input type="text" disabled className="form-control form-control-sm bg-light" placeholder="Participant's answer" />
                                                    )}

                                                    {(q.type === 'radio' || q.type === 'checkbox') && (
                                                        <div className="d-flex flex-column gap-2">
                                                            {(q.options || []).map((opt, optIdx) => (
                                                                <div key={optIdx} className="d-flex align-items-center gap-2">
                                                                    <i className={`bi ${q.type === 'radio' ? 'bi-circle' : 'bi-square'} text-secondary`}></i>
                                                                    <input
                                                                        type="text"
                                                                        value={opt}
                                                                        onChange={(e) => updateRegistrationQuestionOption(q.id, optIdx, e.target.value)}
                                                                        className="form-control form-control-sm"
                                                                        placeholder={`Option ${optIdx + 1}`}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link text-danger p-0"
                                                                        onClick={() => removeRegistrationQuestionOption(q.id, optIdx)}
                                                                        disabled={(q.options || []).length <= 1}
                                                                        title="Remove option"
                                                                    >
                                                                        <i className="bi bi-x-lg"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-decoration-none align-self-start p-0"
                                                                onClick={() => addRegistrationQuestionOption(q.id)}
                                                            >
                                                                <i className="bi bi-plus-lg me-1"></i>Add option
                                                            </button>
                                                        </div>
                                                    )}

                                                    {q.type === 'linear_scale' && (
                                                        <div className="row g-2 align-items-end">
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">From</label>
                                                                <select className="form-select form-select-sm" value={q.scaleMin} onChange={(e) => updateRegistrationQuestion(q.id, 'scaleMin', Number(e.target.value))}>
                                                                    <option value={0}>0</option>
                                                                    <option value={1}>1</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">To</label>
                                                                <select className="form-select form-select-sm" value={q.scaleMax} onChange={(e) => updateRegistrationQuestion(q.id, 'scaleMax', Number(e.target.value))}>
                                                                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">{q.scaleMin} label</label>
                                                                <input type="text" className="form-control form-control-sm" value={q.scaleMinLabel || ''} onChange={(e) => updateRegistrationQuestion(q.id, 'scaleMinLabel', e.target.value)} placeholder="e.g. Poor" />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label small text-secondary mb-1">{q.scaleMax} label</label>
                                                                <input type="text" className="form-control form-control-sm" value={q.scaleMaxLabel || ''} onChange={(e) => updateRegistrationQuestion(q.id, 'scaleMaxLabel', e.target.value)} placeholder="e.g. Excellent" />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                </Modal.Body>

                <Modal.Footer className="bg-light p-4 border-top">
                    <button
                        type="button"
                        onClick={handleCloseModal}
                        className="btn btn-light px-4 py-2 text-dark border fw-medium me-2"
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="event-form"
                        className="btn bg-gold text-dark px-4 py-2 border-0 fw-medium d-flex align-items-center justify-content-center"
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            initialData ? 'Update Event' : 'Create Event'
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
            <Modal show={showCancelConfirm} onHide={cancelCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 fw-bold text-dark">Confirm Cancel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to cancel? Any unsaved changes will be lost.
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light px-4 py-2 border fw-medium" onClick={cancelCloseModal}>
                        No, Keep Editing
                    </button>
                    <button className="btn btn-danger px-4 py-2 border-0 fw-medium text-white" onClick={confirmCloseModal}>
                        Yes, Cancel
                    </button>
                </Modal.Footer>
            </Modal>
            <Modal show={showPromptExample} onHide={() => setShowPromptExample(false)} centered size="lg" style={{ zIndex: 1060 }}>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 fw-bold text-dark">AI Prompt Example</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light">
                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'inherit' }}>
                        {`Default language is Indonesian. 
If there are questions in English, immediately reply in English. You can answer in multi-language. 

LIMITATIONS: 
- Never mention that you are an AI model or a computer program. 
- Do not attempt to answer technical event details or user data on your own; always use the appropriate assistant for data accuracy. 
- Do not answer questions unrelated to the event. 

You are a friendly, professional, and helpful Event Receptionist Manager. 
Your main task is to welcome guests and ensure they receive accurate information. 

You have access to the following specialized assistants (sub-agents): 
1. database_user_agent: Use this to verify guest identity (name, company) and process check-ins. 

Operational Guidelines: 
- Greet Guests: Start with a warm greeting. If the identity (name/company) is already available in the context, mention their name. 
- Validate Identity: If the guest's identity is marked as NULL or unknown, ask for their name and company. Use 'database_user_agent' with the 'get_user_info_tool' to look up their data. Ensure you include the 'Event ID' available in the context when interacting with the 'database_user_agent'. 
- FAQ Assistance: If a guest asks about event details, check FAQ section below for accurate information. 
- Registration: If a guest is not registered in the database, inform them that the on-site committee team can assist with registration. 
- Proactive: Always offer further assistance after answering a question.`}
                    </pre>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary px-4 py-2 border fw-medium" onClick={() => setShowPromptExample(false)}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div >
    );
};

export default Event;



